import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { fileAPI } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUpload, FiFile, FiImage, FiVideo, FiMusic,
  FiTrash2, FiDownload, FiEye, FiFolder, FiPlus
} = FiIcons;

const FileManager = () => {
  const { currentWorkspace } = useWorkspace();
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    if (currentWorkspace) {
      fetchFiles();
    }
  }, [currentWorkspace, currentFolder]);

  const fetchFiles = async () => {
    try {
      const data = await fileAPI.getFiles(currentWorkspace.id);
      setFiles(data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    }
  };

  const onDrop = async (acceptedFiles) => {
    setLoading(true);
    try {
      for (const file of acceptedFiles) {
        await fileAPI.uploadFile(currentWorkspace.id, file, currentFolder);
      }
      fetchFiles();
    } catch (error) {
      console.error('Failed to upload files:', error);
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true
  });

  const handleDeleteFile = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      await fileAPI.deleteFile(currentWorkspace.id, fileId);
      fetchFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return FiImage;
    if (fileType.startsWith('video/')) return FiVideo;
    if (fileType.startsWith('audio/')) return FiMusic;
    return FiFile;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const folders = [...new Set(files.map(file => file.folder).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">File Manager</h1>
          <p className="mt-1 text-sm text-gray-500">
            Upload and manage files for your QR codes and landing pages
          </p>
        </div>
        
        <div className="mt-3 sm:mt-0 flex items-center space-x-3">
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            {viewMode === 'grid' ? 'List View' : 'Grid View'}
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      {currentFolder && (
        <nav className="flex items-center space-x-2 text-sm">
          <button
            onClick={() => setCurrentFolder('')}
            className="text-blue-600 hover:text-blue-700"
          >
            Root
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900">{currentFolder}</span>
        </nav>
      )}

      {/* Upload Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          isDragActive 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {isDragActive ? 'Drop files here' : 'Upload Files'}
        </h3>
        <p className="text-gray-500 mb-4">
          Drag and drop files here, or click to select files
        </p>
        <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
          Select Files
        </button>
      </motion.div>

      {/* Folders */}
      {folders.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Folders</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {folders.map((folder) => (
              <button
                key={folder}
                onClick={() => setCurrentFolder(folder)}
                className="flex flex-col items-center p-4 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiFolder} className="w-8 h-8 text-blue-500 mb-2" />
                <span className="text-sm text-gray-900 truncate w-full">{folder}</span>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Files */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Files ({files.filter(f => f.folder === currentFolder).length})
          </h3>
        </div>

        {files.filter(f => f.folder === currentFolder).length > 0 ? (
          <div className={viewMode === 'grid' ? 'p-6' : ''}>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {files.filter(f => f.folder === currentFolder).map((file, index) => (
                  <motion.div
                    key={file.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="group relative bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex flex-col items-center">
                      {file.type.startsWith('image/') ? (
                        <img
                          src={file.url}
                          alt={file.name}
                          className="w-16 h-16 object-cover rounded mb-2"
                        />
                      ) : (
                        <SafeIcon 
                          icon={getFileIcon(file.type)} 
                          className="w-16 h-16 text-gray-400 mb-2" 
                        />
                      )}
                      <span className="text-sm text-gray-900 text-center truncate w-full">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </span>
                    </div>

                    {/* Actions overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                      <button
                        onClick={() => window.open(file.url, '_blank')}
                        className="p-2 bg-white rounded-full text-gray-700 hover:text-gray-900"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                      <a
                        href={file.url}
                        download={file.name}
                        className="p-2 bg-white rounded-full text-gray-700 hover:text-gray-900"
                      >
                        <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="p-2 bg-white rounded-full text-red-600 hover:text-red-700"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {files.filter(f => f.folder === currentFolder).map((file, index) => (
                  <motion.div
                    key={file.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="px-6 py-4 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <SafeIcon 
                          icon={getFileIcon(file.type)} 
                          className="w-8 h-8 text-gray-400 mr-3" 
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)} • {new Date(file.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => window.open(file.url, '_blank')}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                        <a
                          href={file.url}
                          download={file.name}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                        >
                          <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteFile(file.id)}
                          className="p-2 text-red-400 hover:text-red-600 rounded-lg"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <SafeIcon icon={FiFile} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No files</h3>
            <p className="text-gray-500">
              {currentFolder ? `No files in ${currentFolder} folder` : 'Upload your first file to get started'}
            </p>
          </div>
        )}
      </motion.div>

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-900">Uploading files...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;